import type { Column, Row, RowData } from '@tanstack/react-table';
import { NUMBER_FILTER_OPERATION } from './operation.js';
import { type NumberFilterSchema, numberFilterSchema, numberValueSchema } from './schema.js';

export function typedSetNumberColumnFilterValue<TData extends RowData>(
    column: Column<TData, unknown>,
    filterValue: NumberFilterSchema
) {
    column.setFilterValue(filterValue);
}

export function numberFilter<TData extends RowData>(
    row: Row<TData>,
    columnId: string,
    filterValue: NumberFilterSchema
) {
    const filter = numberFilterSchema.parse(filterValue);
    const value = numberValueSchema.parse(row.getValue(columnId));

    if (filter.operation === NUMBER_FILTER_OPERATION.inArray.id) {
        const isBlank = value === null || value === undefined;

        if (filter.includeBlanks && isBlank) {
            return true;
        }

        if (isBlank) {
            return false;
        }

        return filter.array?.includes(value);
    }

    if (value === null || value === undefined) {
        // TODO: Decide how we handle empty values

        return false;
    } else if (
        (filter.operation === NUMBER_FILTER_OPERATION.between.id &&
            filter.value1 === undefined &&
            filter.value2 === undefined) ||
        (filter.operation !== NUMBER_FILTER_OPERATION.between.id && filter.value1 === undefined)
    ) {
        return true;
    }

    switch (filter.operation) {
        case NUMBER_FILTER_OPERATION.equals.id: {
            return filter.value1 !== undefined && value === filter.value1;
        }
        case NUMBER_FILTER_OPERATION.doesNotEqual.id: {
            return filter.value1 !== undefined && value !== filter.value1;
        }
        case NUMBER_FILTER_OPERATION.greaterThan.id: {
            return filter.value1 !== undefined && value > filter.value1;
        }
        case NUMBER_FILTER_OPERATION.greaterThanOrEqual.id: {
            return filter.value1 !== undefined && value >= filter.value1;
        }
        case NUMBER_FILTER_OPERATION.lessThan.id: {
            return filter.value1 !== undefined && value < filter.value1;
        }
        case NUMBER_FILTER_OPERATION.lessThanOrEqual.id: {
            return filter.value1 !== undefined && value <= filter.value1;
        }
        case NUMBER_FILTER_OPERATION.between.id: {
            const { value1: from, value2: to } = filter;

            if (from !== undefined && to !== undefined) {
                return value >= from && value <= to;
            } else if (from !== undefined) {
                return value >= from;
            } else if (to !== undefined) {
                return value <= to;
            } else {
                return true;
            }
        }
        default: {
            return false;
        }
    }
}
